/**
 * Moderator Tickets Page
 * Interface for moderators to manage tickets (pool, claimed, assigned)
 */

import { useState, useEffect } from 'react'
import { Box, Container, Typography, Button, Tabs, Tab, Alert, ToggleButtonGroup, ToggleButton } from '@mui/material'
import { Refresh, ViewModule, ViewList } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { getTicketPool, getMyClaimedTickets, getMyAssignedTickets } from '@api/services/tickets.api'
import { TicketSummaryDto } from '@api/types/tickets'
import { TicketList } from '../components/TicketList'
import { TicketTable } from '../components/TicketTable'

type ModeratorTab = 'pool' | 'claimed' | 'assigned'
type ViewMode = 'cards' | 'table'

export default function ModeratorTicketsPage() {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState<TicketSummaryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ModeratorTab>('pool')
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    // Load saved preference from localStorage
    const saved = localStorage.getItem('ticketViewMode')
    return (saved as ViewMode) || 'cards'
  })

  const loadTickets = async () => {
    setLoading(true)
    setError(null)
    try {
      let data: TicketSummaryDto[]
      switch (activeTab) {
        case 'pool':
          data = await getTicketPool()
          break
        case 'claimed':
          data = await getMyClaimedTickets()
          break
        case 'assigned':
          data = await getMyAssignedTickets()
          break
        default:
          data = []
      }
      setTickets(data)
    } catch (err: any) {
      console.error('Failed to load tickets:', err)
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [activeTab])

  const handleTabChange = (event: React.SyntheticEvent, newValue: ModeratorTab) => {
    setActiveTab(newValue)
  }

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode)
      // Save preference to localStorage
      localStorage.setItem('ticketViewMode', newMode)
    }
  }

  const handleTicketClick = (ticketId: string) => {
    navigate(`/tickets/${ticketId}`)
  }

  const getEmptyMessage = () => {
    switch (activeTab) {
      case 'pool':
        return 'No tickets in the pool. Great work!'
      case 'claimed':
        return 'You have not claimed any tickets'
      case 'assigned':
        return 'You have no assigned tickets'
      default:
        return 'No tickets found'
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Ticket Management</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="cards" aria-label="card view">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button variant="outlined" startIcon={<Refresh />} onClick={loadTickets} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Ticket Pool" value="pool" />
          <Tab label="My Claimed" value="claimed" />
          <Tab label="Assigned to Me" value="assigned" />
        </Tabs>
      </Box>

      {/* Ticket View - Cards or Table */}
      {viewMode === 'cards' ? (
        <TicketList
          tickets={tickets}
          loading={loading}
          error={error}
          emptyMessage={getEmptyMessage()}
          onTicketClick={handleTicketClick}
          showAssignee={activeTab === 'pool'}
        />
      ) : (
        <TicketTable
          tickets={tickets}
          loading={loading}
          error={error}
          emptyMessage={getEmptyMessage()}
          onTicketClick={handleTicketClick}
          showAssignee={activeTab === 'pool'}
        />
      )}
    </Container>
  )
}
